(function(angular) {
    'use strict';

    var teaspoonsToMlMultiplier = 4.92892;
    var tablespoonsToMlMultiplier = 14.7868;
    var ozToMlMultiplier = 29.57353;

    angular.module('aprima-provider-api').factory('DosingService', [
        '$q',
        '_',
        'AprimaAnalytics',
        'AprimaCache',
        'AprimaRestangular',
        'DoseRangeInfo',
        'DoseRangeUtil',
        'Guid',
        'NumUtil',
        'ServerInfoRepository',
        'SystemGuid',
        DosingService,
    ]);

    function DosingService(
        $q,
        _,
        AprimaAnalytics,
        AprimaCache,
        AprimaRestangular,
        DoseRangeInfo,
        DoseRangeUtil,
        Guid,
        NumUtil,
        ServerInfoRepository,
        SystemGuid
    ) {
        
        var service = {
            convertToCommonUnit: convertToCommonUnit,
            getDoseRangeInfo: getDoseRangeInfo,
            getMinMaxWarning: getMinMaxWarning,
            getDosingDetailsFull: getDosingDetailsFull,
            getVariableDoseRanges: getVariableDoseRanges,
            mapDoseParams: mapDoseParams
        };

        return service;

        function getDoseRangeInfo(
            ageInDays,
            medId,
            gcnSeqNo,
            weightKg,
            heightCm,
            gestationalAge,
            doseTypeIndicator,
            icdCode
        ) {
            //only fill in gcnSeqNo if no medId
            gcnSeqNo = medId ? null : gcnSeqNo;
            
            var params = {
                AgeInDays: ageInDays || 0,
                MedId: medId,
                GcnSeqNo: gcnSeqNo,
                WeightKg: weightKg,
                HeightCm: heightCm,
                GestationalAgeInWeeks: gestationalAge,
                DoseTypeIndicator: doseTypeIndicator,
                IcdCode: icdCode,
            };

            var promise = AprimaRestangular.get()
                .all('dose-range-info')
                .withHttpConfig({cache: AprimaCache.Default.httpMode})
                .getList(params)
                .then(function(data) {
                    var doseRanges = _.compact(data);

                    if(_.isEmpty(doseRanges)) {
                        return $q.reject('First Databank does not contain max/min dosing information for the selected medication.');
                    }

                    var result = doseRanges
                        .map(function(item) {
                            return DoseRangeInfo.mapFromDto(item);
                        });
                    // TODO: handle case where there can be multiple results in this list that comes back
                    // Right now, the backend doesn't support returning more than 1 result, but it could in the future
                    var singleDosingInfo = result[0];
                    return singleDosingInfo;
                });

            return promise;
        }

        function getMinMaxWarning(sigInstructions, rxParameters, patientDetails) {
            var dosageUnits = sigInstructions && sigInstructions.unit && sigInstructions.unit.Id;
            var drugDoseFormId =  rxParameters && rxParameters.drug && rxParameters.drug.UnitId;
            if (!drugDoseFormId) {
                return $q.when('Selected drug does not have dosing units. Unable to calculate recommended daily dosing.');
            }
            
            if(!areCompatibleUnits(dosageUnits, drugDoseFormId)) {
                return $q.when('Dosing units are not compatible with drug. Unable to calculate recommended daily dosing.');
            }

            var params = mapDoseParams(sigInstructions, rxParameters, patientDetails);

            //convert to mg, ml, or leave as tablet/capsule
            params.dosageAmount = convertToCommonUnit(dosageUnits, params.dosageAmount);

            return getDoseRangeInfo(
                params.ageInDays, 
                params.medId, 
                params.gcnSeqNo,
                params.weight, 
                params.height, 
                params.gestationalAge,
                params.doseTypeIndicator, 
                params.icdCode
            )
                .then(function(singleDosingInfo) {
                    var minMaxWarningText = DoseRangeUtil.GetMaxMinWarningMsg(singleDosingInfo, params.dosageAmount, params.frequencyPerDay, params.durationOfTherapyInDays);
                    
                    return minMaxWarningText;
                });
        }

        function mapDoseParams(sigInstructions, rxParameters, patientDetails){
            var params = {};
            
            params.frequencyPerDay = sigInstructions && sigInstructions.frequency && sigInstructions.frequency.DosesPerDay;
            params.visitId = null;

            if(rxParameters) {
                var drug = rxParameters.drug;
                if(drug) {
                    params.medId = drug.MedId;
                    params.gcnSeqNo = drug.GcnSeqNo;
                }

                params.patientId = rxParameters.patientId;
                params.visitId = rxParameters.visit && rxParameters.visit.VisitId;
            }

            // this is a newer concept, and we have no current way to derive it, so just leave it as null
            params.gestationalAge = null;

            // this is a newer concept, and we have no current way to derive it, so just leave it as null
            params.doseTypeIndicator = null;

            // TODO: fill in iceCode with something from sigwriter
            // but it has to be a single value
            params.icdCode = null;

            params.weight = null;
            params.weightDate = null;
            params.height = null;
            params.heightDate = null;
            params.ageInDays = null;
            if(patientDetails) {
                params.weight = patientDetails.WeightInKg;
                params.weightDate = patientDetails.WeightPerformedDate;
                params.height = patientDetails.HeightInCm;
                params.heightDate = patientDetails.HeightPerformedDate;
                params.ageInDays = patientDetails.AgeInDays;
            }
            
            params.durationOfTherapyInDays = null;
            if (sigInstructions && sigInstructions.startDate && sigInstructions.endDate){
                params.durationOfTherapyInDays = moment(sigInstructions.endDate).diff(moment(sigInstructions.startDate), 'days');
            }

            params.dosageAmount = sigInstructions.dosage && sigInstructions.dosage.Name;

            return params;
        }

        function convertToCommonUnit(dosageUnits, dosageAmount) {
            switch (dosageUnits) {
                case SystemGuid.medicationUnitTablets:
                case SystemGuid.medicationUnitCapsules:
                    break;
                // converts these to ml
                case SystemGuid.medicationUnitTeaspoons: 
                    dosageAmount = NumUtil.multiply(dosageAmount, teaspoonsToMlMultiplier);
                    break;
                case SystemGuid.medicationUnitTablespoons:
                    dosageAmount = NumUtil.multiply(dosageAmount, tablespoonsToMlMultiplier);
                    break;
                case SystemGuid.medicationUnitOz:
                    dosageAmount = NumUtil.multiply(dosageAmount, ozToMlMultiplier);
                    break;
                case SystemGuid.medicationUnitPuffs: 
                case SystemGuid.medicationUnitDrops:
                case SystemGuid.medicationUnitMilliliters:
                    break;
                // converts these to mg
                case SystemGuid.medicationUnitGrams:
                    dosageAmount = NumUtil.multiply(dosageAmount, 1000);
                    break;
                case SystemGuid.medicationUnitMicrograms:
                    dosageAmount = NumUtil.divide(dosageAmount, 1000);
                    break;
                case SystemGuid.medicationUnitMilligrams:
                    break;
                default: 
                    break;
            }
            return dosageAmount;
        }

        function getDosingDetailsFull(visitId, patientId, defaultValue) {
            if(!visitId && !patientId) {
                return $q.when(defaultValue);
            }

            var hasComprehensiveFeature = ServerInfoRepository.isFeatureSupported('PatientDosingDetails_Comprehensive');
            if(hasComprehensiveFeature) {
                return getComprehensiveDosingDetails(visitId, patientId);
            }

            if(visitId && visitId !== Guid.empty()) {
                return getVisitDosingDetails(visitId);
            }

            if(patientId && patientId !== Guid.empty()) {
                return getPatientDosingDetails(patientId);
            }

            return $q.when(defaultValue);
        }

        function getComprehensiveDosingDetails(visitId, patientId) {
            var params = {
                VisitId: visitId || null,
                PatientId: patientId || null,
            };

            var promise = AprimaRestangular.get()
                .one('dosing-details')
                .get(params);

            return promise;
        }

        function getPatientDosingDetails(patientId) {
            var promise = AprimaRestangular.get()
                .one('patients', patientId)
                .one('dosing-details')
                .get();

            return promise;
        }

        function getVisitDosingDetails(visitId) {
            var promise = AprimaRestangular.get()
                .one('visits', visitId)
                .one('dosing-details')
                .get();

            return promise;
        }

        function getVariableDoseRanges(gcnSeqNo) {
            var timing = AprimaAnalytics.startTiming('DosingService', 'api', 'getVariableDoseRanges');
            var restangular = AprimaRestangular.get();
            return restangular.one('variable-dose-ranges')
                              .withHttpConfig({cache: AprimaCache.Default.httpMode})
                              .get({
                                  gcnSeqNo: gcnSeqNo,
                              })
                              .finally(timing.stop);
        }

        function areCompatibleUnits(dosageUnits, drugDoseFormId) {
            var dosageUnitsBase = getBaseUnitFromSelection(dosageUnits);
            var drugDoseFormIdBase = getBaseUnitForDrug(drugDoseFormId);
            return drugDoseFormIdBase !== null && dosageUnitsBase === drugDoseFormIdBase;
        }

        function getBaseUnitFromSelection(unitId) {
            var baseline; 
            switch (unitId) {
                case SystemGuid.medicationUnitTablets:
                case SystemGuid.medicationUnitCapsules:
                        baseline = SystemGuid.medicationUnitTablets;
                        break;
                case SystemGuid.medicationUnitPuffs: 
                case SystemGuid.medicationUnitDrops:
                case SystemGuid.medicationUnitTeaspoons: 
                case SystemGuid.medicationUnitTablespoons:
                case SystemGuid.medicationUnitOz:
                case SystemGuid.medicationUnitMilliliters:
                case SystemGuid.medicationUnitSpray:
                        baseline = SystemGuid.medicationUnitMilliliters;
                        break;
                
                case SystemGuid.medicationUnitGrams:
                case SystemGuid.medicationUnitMilligrams:
                case SystemGuid.medicationUnitMicrograms:
                        baseline = SystemGuid.medicationUnitMilligrams;
                        break;
                default: 
                    baseline = unitId;

            }
            return baseline;
        }
        function getBaseUnitForDrug(unitId) {
            // units that do not need conversion 
            var baseline; 
            switch (unitId) {
                case SystemGuid.medicationUnitTablets:
                case SystemGuid.medicationUnitCapsules:
                        baseline = SystemGuid.medicationUnitTablets;
                        break;
                case SystemGuid.medicationUnitPuffs: 
                case SystemGuid.medicationUnitDrops:
                case SystemGuid.medicationUnitMilliliters:
                case SystemGuid.medicationUnitSpray:
                        baseline = SystemGuid.medicationUnitMilliliters;
                        break;
                case SystemGuid.medicationUnitMilligrams:
                        baseline = SystemGuid.medicationUnitMilligrams;
                        break;
                default: 
                    baseline = unitId;

            }
            return baseline;
        }
    }
})(window.angular);
